// src/state-store.ts
var StateStore = class {
  elementStore;
  constructor() {
    this.elementStore = [];
  }
  storeStyles(elements, styles) {
    this.applyToElements(elements, (el, elementStore) => {
      const computedStyles = getComputedStyle(el);
      styles.forEach(
        (p) => elementStore.styles[p] = computedStyles.getPropertyValue(p)
      );
    });
  }
  storeAttributes(elements, attributes) {
    this.applyToElements(elements, (el, elementStore) => {
      attributes.forEach(
        (attribute) => elementStore.attributes[attribute] = el.getAttribute(attribute)
      );
    });
  }
  revert() {
    this.elementStore.forEach((elementStore) => {
      Object.keys(elementStore.styles).forEach(
        (property) => elementStore.el.style.setProperty(
          property,
          elementStore.styles[property]
        )
      );
      Object.keys(elementStore.attributes).forEach((attribute) => {
        const cachedValue = elementStore.attributes[attribute];
        if (attribute === null || attribute === "") {
          elementStore.el.removeAttribute(attribute);
        } else if (cachedValue !== null) {
          elementStore.el.setAttribute(attribute, cachedValue);
        }
      });
    });
    this.elementStore = [];
  }
  applyToElements(elements, fn) {
    ;
    (Array.isArray(elements) ? elements : [elements]).forEach((el) => {
      let elementStore = this.elementStore.find((s) => s.el === el);
      if (!elementStore) {
        elementStore = { el, styles: {}, attributes: {} };
        this.elementStore.push(elementStore);
      }
      fn(el, elementStore);
    });
  }
};

// src/responder.ts
var boxSliders = [];
var resizeObserver;
if (typeof ResizeObserver !== "undefined") {
  resizeObserver = new ResizeObserver((sliderElements) => {
    sliderElements.forEach(
      (sliderEl) => boxSliders.find((bs) => bs.el === sliderEl.target)?.reset()
    );
  });
}
var responder = {
  add(boxSlider) {
    boxSliders.push(boxSlider);
    resizeObserver?.observe(boxSlider.el);
  },
  remove(boxSlider) {
    resizeObserver?.unobserve(boxSlider.el);
    boxSliders = boxSliders.filter((b) => b !== boxSlider);
  }
};

// src/transition-queue.ts
var createQueue = () => {
  const queue = [];
  const run = () => {
    const fn = queue[0];
    if (fn) {
      fn().then(() => {
        queue.shift();
        run();
      });
    }
  };
  return {
    push(fn) {
      queue.push(fn);
      if (queue.length === 1) {
        run();
      }
    },
    clear() {
      queue.length = 0;
    }
  };
};

// src/box-slider.ts
var defaultOptions = Object.freeze({
  autoScroll: true,
  pauseOnHover: false,
  speed: 800,
  startIndex: 0,
  swipe: true,
  swipeTolerance: 30,
  timeout: 5e3
});
var BoxSlider = class {
  _activeIndex;
  _el;
  _effect;
  _stateStore;
  options;
  slides;
  autoScrollTimer;
  eventListeners;
  elListeners;
  isDestroyed;
  transitionQueue;
  get activeIndex() {
    return this._activeIndex;
  }
  get el() {
    if (this._el === void 0) {
      throw new Error("Slider element is null");
    }
    return this._el;
  }
  get stateStore() {
    if (this._stateStore === void 0) {
      throw new Error(
        "State store is null, are you trying to interact with a destroyed slider?"
      );
    }
    return this._stateStore;
  }
  get effect() {
    if (this._effect === void 0) {
      throw new Error(
        "Slide effect is null, are you trying to interact with a destroyed slider?"
      );
    }
    return this._effect;
  }
  constructor(el, effect, options) {
    this._el = el;
    this._stateStore = new StateStore();
    this.transitionQueue = createQueue();
    this.slides = [];
    this.eventListeners = {};
    this.elListeners = {};
    this.isDestroyed = false;
    this.options = {
      autoScroll: true,
      speed: 800,
      timeout: 5e3,
      pauseOnHover: false,
      startIndex: 0,
      swipe: true,
      swipeTolerance: 30,
      ...options
    };
    this._activeIndex = this.options.startIndex;
    this.init(effect);
    if (this.slides.length < this.activeIndex) {
      this.destroy();
      throw new Error(
        `Start index option is out of bounds - slides=${this.slides.length} start=${this.activeIndex}`
      );
    }
    this.applyEventListeners();
    responder.add(this);
  }
  reset(options, effect) {
    this.stopAutoScroll();
    this.effect.destroy && this.effect.destroy(this.el);
    this.stateStore.revert();
    this.options = {
      ...this.options,
      ...options
    };
    if (options?.startIndex !== void 0 && !isNaN(options.startIndex)) {
      this._activeIndex = options.startIndex;
    }
    this.init(effect || this.effect);
  }
  next() {
    return new Promise((resolve) => {
      this.transitionQueue.push(async () => {
        const nextIndex = this.activeIndex === this.slides.length - 1 ? 0 : this.activeIndex + 1;
        await this.transitionTo(nextIndex, false);
        resolve();
      });
    });
  }
  prev() {
    return new Promise((resolve) => {
      this.transitionQueue.push(async () => {
        const prevIndex = this.activeIndex === 0 ? this.slides.length - 1 : this.activeIndex - 1;
        await this.transitionTo(prevIndex, true);
        resolve();
      });
    });
  }
  skipTo(nextIndex, backwards) {
    return new Promise((resolve) => {
      this.transitionQueue.push(async () => {
        await this.transitionTo(
          nextIndex,
          backwards === void 0 ? nextIndex < this.activeIndex : backwards
        );
        resolve();
      });
    });
  }
  pause() {
    if (this.autoScrollTimer) {
      this.stopAutoScroll();
      this.emit("pause");
    }
    return this;
  }
  play() {
    this.setAutoScroll();
    this.emit("play");
    return this;
  }
  addEventListener(ev, callback) {
    if (!Array.isArray(this.eventListeners[ev])) {
      this.eventListeners[ev] = [];
    }
    this.eventListeners[ev].push(callback);
    return this;
  }
  removeEventListener(ev, callback) {
    const listeners = this.eventListeners[ev]?.filter(
      (cb) => cb !== callback
    );
    this.eventListeners[ev] = listeners;
    return this;
  }
  destroy() {
    this.isDestroyed = true;
    this.stopAutoScroll();
    if (this.effect.destroy) {
      this.effect.destroy(this.el);
    }
    this.stateStore.revert();
    this.transitionQueue.clear();
    responder.remove(this);
    this.emit("destroy");
    this.eventListeners = {};
    Object.keys(this.elListeners).forEach(
      (ev) => this.elListeners[ev].forEach(
        (listener) => this.el.removeEventListener(ev, listener)
      )
    );
    this.elListeners = {};
    delete this._el;
    delete this._stateStore;
    delete this._effect;
    this.slides.length = 0;
  }
  init(effect) {
    this._effect = effect;
    this.slides = this.getSlides();
    this.effect.initialize(this.el, this.slides, this.stateStore, {
      ...this.options,
      startIndex: this.activeIndex
    });
    this.addAriaAttributes();
    if (this.options.autoScroll) {
      this.setAutoScroll();
    }
  }
  addAriaAttributes() {
    this.stateStore.storeAttributes(this.el, ["aria-live"]);
    this.el.setAttribute("aria-live", "polite");
    this.stateStore.storeAttributes(this.slides, ["aria-roledescription"]);
    this.slides.forEach(
      (slide) => slide.setAttribute("aria-roledescription", "slide")
    );
  }
  getSlides() {
    return Array.from(this.el.children).filter(
      (child) => child instanceof HTMLElement
    );
  }
  transitionTo(nextIndex, backwards) {
    return new Promise((resolve, reject) => {
      if (this.isDestroyed || nextIndex === this.activeIndex) {
        return resolve();
      }
      if (nextIndex < 0 || nextIndex >= this.slides.length) {
        return reject(new Error(`${nextIndex} is not a valid slide index`));
      }
      this.stopAutoScroll();
      const settings = {
        el: this.el,
        slides: this.slides,
        speed: this.options.speed,
        currentIndex: this.activeIndex,
        isPrevious: backwards,
        nextIndex
      };
      this._activeIndex = nextIndex;
      this.emit("before", {
        currentIndex: settings.currentIndex,
        nextIndex: settings.nextIndex,
        speed: settings.speed
      });
      this.effect.transition(settings).then(() => {
        if (this.options.autoScroll) {
          this.setAutoScroll();
        }
        this.emit("after");
        resolve();
      });
    });
  }
  stopAutoScroll() {
    window.clearTimeout(this.autoScrollTimer);
  }
  emit(ev, payload) {
    const handlers = this.eventListeners[ev];
    if (ev === "destroy") {
      const destroyHandlers = handlers;
      destroyHandlers?.forEach((cb) => cb());
    } else {
      handlers?.forEach(
        (cb) => cb({
          currentIndex: this.activeIndex,
          speed: this.options.speed,
          ...payload
        })
      );
    }
  }
  setAutoScroll() {
    this.stopAutoScroll();
    window.requestAnimationFrame(() => {
      this.el.setAttribute("aria-live", "off");
      this.autoScrollTimer = window.setTimeout(
        () => this.next(),
        this.options.timeout
      );
    });
  }
  addElListener(ev, handler) {
    this.el.addEventListener(ev, handler);
    this.elListeners[ev] = this.elListeners[ev] || [];
    this.elListeners[ev].push(handler);
  }
  applyEventListeners() {
    this.addElListener("pointerenter", () => {
      if (this.options.autoScroll && this.options.pauseOnHover) {
        this.pause();
      }
    });
    this.addElListener("pointerleave", () => {
      if (this.options.autoScroll && this.options.pauseOnHover) {
        this.play();
      }
    });
    if (this.options.swipe) {
      this.addSwipeNavigation();
    }
  }
  addSwipeNavigation() {
    let pointerTraceX = 0;
    this.addElListener("pointerdown", (ev) => {
      pointerTraceX = ev.clientX;
    });
    this.addElListener("pointerup", (ev) => {
      const distanceX = ev.clientX - pointerTraceX;
      if (Math.abs(distanceX) >= this.options.swipeTolerance) {
        if (distanceX > 0) {
          this.prev();
        } else {
          this.next();
        }
        ev.stopPropagation();
      }
    });
  }
};

// src/utils.ts
function applyCss(el, styles) {
  Object.keys(styles).forEach((key) => el.style.setProperty(key, styles[key]));
}
function locateSlideImageSrc(slide) {
  if (slide.getAttribute("src")) {
    return slide.getAttribute("src");
  }
  const img = slide.querySelector("img");
  return img ? img.currentSrc || img.getAttribute("src") : null;
}

// src/effects/carousel-slider.ts
var SLIDE_STYLES = [
  "left",
  "position",
  "top",
  "transform",
  "transition",
  "visibility",
  "z-index"
];
var BOX_STYLES = ["overflow", "position"];
var CarouselSlider = class {
  options;
  constructor(options) {
    this.options = {
      cover: options?.cover === true,
      timingFunction: options?.timingFunction || "ease-in-out"
    };
  }
  initialize(el, slides, stateStore, options) {
    stateStore.storeStyles(slides, SLIDE_STYLES);
    stateStore.storeStyles(el, BOX_STYLES);
    applyCss(el, {
      overflow: "hidden"
    });
    if ("static inherit".indexOf(getComputedStyle(el).position) !== -1) {
      applyCss(el, { position: "relative" });
    }
    slides.forEach((slide, index) => {
      applyCss(slide, {
        left: "0",
        position: "absolute",
        top: "0",
        transform: `translateX(${slide.offsetWidth}px)`,
        transition: "initial",
        visibility: "hidden",
        "z-index": "1"
      });
      if (index === options.startIndex) {
        applyCss(slide, {
          transform: "translateX(0px)",
          visibility: "visible",
          "z-index": "2"
        });
      }
    });
  }
  transition(settings) {
    return new Promise((resolve) => {
      const currentSlide = settings.slides[settings.currentIndex];
      const currentSlideWidth = `${currentSlide.offsetWidth}px`;
      const nextSlide = settings.slides[settings.nextIndex];
      const nextSlideWidth = `${nextSlide.offsetWidth}px`;
      applyCss(nextSlide, {
        transform: `translateX(${settings.isPrevious ? "-" + nextSlideWidth : nextSlideWidth})`
      });
      setTimeout(() => {
        applyCss(nextSlide, {
          transform: "translateX(0px)",
          transition: `transform ${settings.speed}ms ${this.options.timingFunction}`,
          visibility: "visible",
          "z-index": "2"
        });
        applyCss(currentSlide, {
          transform: this.options.cover ? "translateX(0px)" : `translateX(${settings.isPrevious ? currentSlideWidth : "-" + currentSlideWidth})`,
          transition: this.options.cover ? "initial" : `transform ${settings.speed}ms ${this.options.timingFunction}`,
          visibility: "visible",
          "z-index": "1"
        });
        setTimeout(() => {
          applyCss(currentSlide, {
            transform: `translateX(${currentSlideWidth})`,
            transition: "initial",
            visibility: "hidden"
          });
          resolve();
        }, settings.speed);
      }, 0);
    });
  }
};

// src/effects/cube-slider.ts
var SLIDE_STYLES2 = ["left", "position", "top", "transform", "z-index"];
var BOX_STYLES2 = [
  "left",
  "overflow",
  "position",
  "top",
  "transform",
  "transform-style",
  "transition",
  "z-index"
];
var VIEWPORT_STYLES = ["overflow", "perspective", "position"];
var CubeSlider = class {
  options;
  translateZ;
  constructor(options) {
    this.options = {
      direction: options?.direction || "horizontal",
      perspective: options?.perspective || 1e3
    };
  }
  initialize(el, slides, stateStore, options) {
    const width = el.offsetWidth;
    const height = el.offsetHeight;
    const perspective = `${this.options.perspective}px`;
    const viewport = el.parentElement;
    if (viewport == null) {
      throw new Error("Unable to locate viewport element for Cube slider");
    }
    this.translateZ = this.options.direction === "vertical" ? height / 2 : width / 2;
    stateStore.storeStyles(slides, SLIDE_STYLES2);
    stateStore.storeStyles(el, BOX_STYLES2);
    stateStore.storeStyles(viewport, VIEWPORT_STYLES);
    slides.forEach(
      (slide) => applyCss(slide, {
        left: "0",
        position: "absolute",
        top: "0"
      })
    );
    applyCss(el, {
      left: "0",
      overflow: "visible",
      position: "absolute",
      top: "0"
    });
    if (["absolute", "fixed", "relative"].indexOf(
      getComputedStyle(viewport).position
    ) === -1) {
      applyCss(viewport, { position: "relative" });
    }
    applyCss(viewport, {
      overflow: "visible",
      perspective
    });
    applyCss(slides[options.startIndex || 0], {
      transform: `${this.rotation(0)} translate3d(0, 0, ${this.translateZ}px)`
    });
    applyCss(el, {
      "transform-style": "preserve-3d",
      transform: `translate3d(0, 0, -${this.translateZ}px)`
    });
    setTimeout(
      () => applyCss(el, {
        transition: `transform ${options.speed}ms`
      }),
      50
    );
  }
  transition(settings) {
    return new Promise((resolve) => {
      const angle = settings.isPrevious ? 90 : -90;
      applyCss(settings.slides[settings.nextIndex], {
        transform: `${this.rotation(-angle)} translate3d(0, 0, ${this.translateZ}px)`,
        "z-index": "2"
      });
      applyCss(settings.el, {
        transition: `transform ${settings.speed}ms`,
        transform: `translate3d(0, 0, -${this.translateZ}px) ${this.rotation(
          angle
        )}`
      });
      setTimeout(() => {
        settings.slides.forEach((s, index) => {
          if (index !== settings.nextIndex) {
            applyCss(s, { transform: "initial" });
          }
        });
        applyCss(settings.el, {
          transition: "initial",
          transform: `translate3d(0, 0, -${this.translateZ}px) ${this.rotation(
            0
          )}`
        });
        applyCss(settings.slides[settings.nextIndex], {
          transform: `${this.rotation(0)} translate3d(0, 0, ${this.translateZ}px)`,
          "z-index": "1"
        });
        resolve();
      }, settings.speed);
    });
  }
  rotation(angle) {
    return `rotate3d(${this.options.direction === "vertical" ? "1, 0, 0" : "0, 1, 0"}, ${angle}deg)`;
  }
};

// src/effects/fade-slider.ts
var SLIDE_STYLES3 = [
  "display",
  "height",
  "left",
  "opacity",
  "position",
  "top",
  "transition",
  "width",
  "z-index"
];
var BOX_STYLES3 = ["height", "overflow", "position"];
var FadeSlider = class {
  options;
  constructor(options) {
    this.options = {
      timingFunction: options?.timingFunction || "ease-in"
    };
  }
  initialize(el, slides, styleStore, options) {
    styleStore.storeStyles(slides, SLIDE_STYLES3);
    styleStore.storeStyles(el, BOX_STYLES3);
    if ("static inherit".indexOf(getComputedStyle(el).position) !== -1) {
      applyCss(el, { position: "relative" });
    }
    applyCss(el, {
      height: `${slides[options.startIndex || 0].offsetHeight}px`,
      overflow: "hidden"
    });
    slides.forEach((slide, index) => {
      applyCss(slide, {
        height: "100%",
        left: "0",
        opacity: "2",
        position: "absolute",
        top: "0",
        transition: `opacity ${options.speed}ms ${this.options.timingFunction}`,
        width: "100%",
        "z-index": "2"
      });
      if (index !== options.startIndex) {
        applyCss(slide, { opacity: "0", "z-index": "1" });
      }
    });
  }
  transition(settings) {
    return new Promise((resolve) => {
      const currentSlide = settings.slides[settings.currentIndex];
      const nextSlide = settings.slides[settings.nextIndex];
      applyCss(currentSlide, { "z-index": "1", opacity: "0" });
      applyCss(nextSlide, { "z-index": "2", opacity: "1" });
      setTimeout(resolve, settings.speed);
    });
  }
};

// src/effects/tile/fade-transition.ts
var FadeTransition = class {
  createTile(tileSettings) {
    const tile = document.createElement("div");
    const front = document.createElement("div");
    const back = document.createElement("div");
    const faces = [front, back];
    tile.classList.add(tileSettings.tileClass);
    applyCss(tile, {
      height: `${tileSettings.height}px`,
      left: `${tileSettings.fromLeft}px`,
      position: "absolute",
      top: `${tileSettings.fromTop}px`,
      width: `${tileSettings.width}px`
    });
    faces.forEach(
      (face) => applyCss(face, {
        "background-position": `-${tileSettings.fromLeft}px -${tileSettings.fromTop}px`,
        "background-size": `${tileSettings.boxWidth}px ${tileSettings.boxHeight}px`,
        height: `${tileSettings.height}px`,
        left: "0",
        position: "absolute",
        top: "0",
        transition: "opacity 400ms",
        width: `${tileSettings.width}px`
      })
    );
    applyCss(front, { "background-image": `url(${tileSettings.imgSrc})` });
    applyCss(back, { opacity: "0" });
    front.classList.add(tileSettings.frontClass);
    tile.appendChild(front);
    back.classList.add(tileSettings.backClass);
    tile.appendChild(back);
    return tile;
  }
  transition(tile, nextFace) {
    const front = tile.querySelector(".front");
    const back = tile.querySelector(".back");
    applyCss(front, { opacity: nextFace === "front" ? "1" : "0" });
    applyCss(back, { opacity: nextFace === "front" ? "0" : "1" });
  }
};
var fade_transition_default = FadeTransition;

// src/effects/tile/flip-transition.ts
var FlipTransition = class {
  createTile(tileSettings) {
    const tileHolder = document.createElement("div");
    const tile = document.createElement("div");
    const front = document.createElement("div");
    const back = document.createElement("div");
    const faces = [front, back];
    applyCss(tileHolder, {
      height: `${tileSettings.height}px`,
      left: `${tileSettings.fromLeft}px`,
      perspective: "400px",
      position: "absolute",
      top: `${tileSettings.fromTop}px`,
      width: `${tileSettings.width}px`
    });
    tile.classList.add(tileSettings.tileClass);
    applyCss(tile, {
      height: `${tileSettings.height}px`,
      "transform-style": "preserve-3d",
      transition: "transform 400ms",
      width: `${tileSettings.width}px`
    });
    tileHolder.appendChild(tile);
    applyCss(front, { "background-image": `url(${tileSettings.imgSrc})` });
    faces.forEach(
      (face) => applyCss(face, {
        "background-position": `-${tileSettings.fromLeft}px -${tileSettings.fromTop}px`,
        "background-size": `${tileSettings.boxWidth}px ${tileSettings.boxHeight}px`,
        "backface-visibility": "hidden",
        height: `${tileSettings.height}px`,
        left: "0",
        position: "absolute",
        top: "0",
        width: `${tileSettings.width}px`
      })
    );
    front.classList.add(tileSettings.frontClass);
    tile.appendChild(front);
    back.classList.add(tileSettings.backClass);
    tile.appendChild(back);
    applyCss(back, { transform: "rotateY(180deg)" });
    return tileHolder;
  }
  transition(tile, nextFace) {
    applyCss(tile, {
      transform: `rotate3d(0, 1, 0, ${nextFace === "back" ? 180 : 0}deg)`
    });
  }
};
var flip_transition_default = FlipTransition;

// src/effects/tile/tile-slider.ts
var defaultOptions2 = {
  tileEffect: "flip",
  rows: 8,
  rowOffset: 50
};
var TILE_CLASS = "bs-tile";
var SLIDE_STYLES4 = [
  "position",
  "overflow",
  "clip",
  "height",
  "width",
  "margin",
  "padding",
  "border"
];
var BOX_STYLES4 = ["height", "overflow", "position"];
var TileSlider = class {
  rowCount;
  rowOffset;
  tileEffect;
  _tileWrapper;
  tileTransition;
  grid;
  activeFace;
  get tileWrapper() {
    if (this._tileWrapper === void 0) {
      throw new Error("TileWrapper is undefined");
    }
    return this._tileWrapper;
  }
  constructor(options) {
    this.tileEffect = options?.tileEffect || defaultOptions2.tileEffect;
    this.rowCount = options?.rows || defaultOptions2.rows;
    this.rowOffset = options?.rowOffset || defaultOptions2.rowOffset;
    this.activeFace = "front";
    this.tileTransition = this.tileEffect === "fade" ? new fade_transition_default() : new flip_transition_default();
  }
  initialize(el, slides, stateStore, options) {
    const imgSrc = locateSlideImageSrc(slides[options.startIndex || 0]);
    if (imgSrc == null) {
      throw new Error("Unable to locate slide image src for Tile slider");
    }
    const fragment = document.createDocumentFragment();
    stateStore.storeStyles(el, BOX_STYLES4);
    stateStore.storeStyles(slides, SLIDE_STYLES4);
    this.grid = this.calculateGrid(el, slides);
    if (this._tileWrapper) {
      this.destroy(el);
    }
    const tileWrapper = document.createElement("div");
    tileWrapper.setAttribute("aria-hidden", "true");
    el.appendChild(tileWrapper);
    this._tileWrapper = tileWrapper;
    if ("fixed absolute relative".indexOf(getComputedStyle(el).position) === -1) {
      applyCss(el, { position: "relative" });
    }
    applyCss(el, {
      height: `${el.offsetHeight}px`,
      overflow: "hidden"
    });
    applyCss(this.tileWrapper, {
      position: "absolute",
      top: "0",
      left: "0",
      width: "100%",
      height: "100%"
    });
    slides.forEach(
      (s) => applyCss(s, {
        position: "absolute",
        overflow: "hidden",
        clip: "rect(0 0 0 0)",
        height: "1px",
        width: "1px",
        margin: "-1px",
        padding: "0",
        border: "0"
      })
    );
    for (let i = 0; i < this.grid.rows; ++i) {
      const fromTop = i * this.grid.sideLength;
      for (let j = 0; j < this.grid.cols; ++j) {
        fragment.appendChild(
          this.tileTransition.createTile({
            backClass: "back",
            boxWidth: el.offsetWidth,
            boxHeight: el.offsetHeight,
            fromTop,
            fromLeft: j * this.grid.sideLength,
            frontClass: "front",
            imgSrc,
            width: this.grid.sideLength,
            height: this.grid.sideLength,
            tileClass: TILE_CLASS
          })
        );
      }
    }
    this.tileWrapper.appendChild(fragment);
  }
  transition(settings) {
    return new Promise((resolve) => {
      const tiles = this.tileWrapper.querySelectorAll(`.${TILE_CLASS}`);
      const rowInterval = this.rowOffset;
      const tileInterval = (settings.speed - rowInterval * (this.grid.rows - 1)) / this.grid.cols;
      const imgSrc = locateSlideImageSrc(settings.slides[settings.nextIndex]);
      const nextFace = this.activeFace === "front" ? "back" : "front";
      this.tileWrapper.querySelectorAll(`.${nextFace}`).forEach(
        (tile) => applyCss(tile, {
          "background-image": `url(${imgSrc})`
        })
      );
      for (let i = 0; i < this.grid.rows; ++i) {
        let j = i * this.grid.cols;
        let timerIndex = 0;
        const rowEnd = j + this.grid.cols;
        const rowTimeout = i * rowInterval;
        setTimeout(() => {
          for (; j < rowEnd; ++j) {
            const tileTimeout = timerIndex * tileInterval;
            const tile = tiles[j];
            setTimeout(() => {
              this.tileTransition.transition(tile, nextFace);
              if (tile === tiles[tiles.length - 1]) {
                this.activeFace = nextFace;
                resolve();
              }
            }, tileTimeout);
            timerIndex += 1;
          }
        }, rowTimeout);
      }
    });
  }
  destroy(el) {
    el.removeChild(this.tileWrapper);
    delete this._tileWrapper;
  }
  calculateGrid(el, slides) {
    const height = slides[0].offsetHeight;
    const rows = this.rowCount;
    const sideLength = Math.ceil(height / rows);
    const cols = Math.ceil(el.offsetWidth / sideLength);
    return { cols, rows, sideLength, height };
  }
};
export {
  BoxSlider,
  CarouselSlider,
  CubeSlider,
  FadeSlider,
  TileSlider,
  defaultOptions
};
